import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Panel, Pill, inputClass, buttonClass } from "../components/Bits";
import { adminApi, apiError } from "../adminApi";
import { Tabs } from "./AdminPlanScenarios";
import {
  ARRS, DESIGN_F, HSA_YEAR, INDUSTRIES, MODEL_VERSION, SAMPLE, model, money, rank, reason, signed, top3,
} from "../../lib/planModel";
import {
  ASSUMPTIONS, CHECKLIST, DECISION_FACTORS, DESIGN_LAYER, FORMULAS, REF_FIGURES, SOURCES,
} from "../planBuilderRef";

const SECTIONS = [
  ["inputs", "Employer inputs"], ["recommend", "Recommended options"], ["cost", "Annual cost"],
  ["fit", "Fit scores"], ["detail", "Arrangement detail"], ["compliance", "Compliance checks"],
  ["design", "Plan design layer"], ["reference", "Reference figures"], ["assumptions", "Assumptions"],
  ["checklist", "Data to gather"], ["factors", "Decision factors"], ["sources", "Sources"],
];

const label = "block font-mono text-[10px] uppercase tracking-[0.2em] text-cream/45";
const th = "px-3 py-2 text-left font-mono text-[10px] uppercase tracking-[0.2em] text-cream/40";
const td = "px-3 py-2 text-sm text-cream/80";
const k = (v) => `$${Math.round(v / 1000)}k`;

const Head = ({ id, children, note }) => (
  <div id={id} className="scroll-mt-24">
    <h2 className="font-serif text-2xl font-medium tracking-tight text-cream">{children}</h2>
    {note ? <p className="mt-2 max-w-3xl text-sm leading-relaxed text-cream/50">{note}</p> : null}
  </div>
);

const Num = ({ name, value, onChange, children }) => (
  <div>
    <label className={label} htmlFor={`pb-${name}`}>{children}</label>
    <input id={`pb-${name}`} inputMode="numeric" value={value} data-testid={`pb-${name}`}
      onChange={(e) => onChange(e.target.value)} className={`${inputClass} mt-1.5`} />
  </div>
);

const Pick = ({ name, value, onChange, options, children }) => (
  <div>
    <label className={label} htmlFor={`pb-${name}`}>{children}</label>
    <select id={`pb-${name}`} value={value} data-testid={`pb-${name}`}
      onChange={(e) => onChange(e.target.value)} className={`${inputClass} mt-1.5`}>
      {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
    </select>
  </div>
);

export default function AdminPlanBuilder() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const leadId = params.get("lead");
  const [inputs, setInputs] = useState(SAMPLE);
  const [checklist, setChecklist] = useState({});
  const [meta, setMeta] = useState({ id: null, name: "", client_company: "", advisor_notes: "", status: "draft", lead_id: null });
  const [lead, setLead] = useState(null);
  const [storedVersion, setStoredVersion] = useState(null);
  const [detail, setDetail] = useState("level");
  const [saving, setSaving] = useState(false);

  const set = (patch) => setInputs((s) => ({ ...s, ...patch }));
  const m = useMemo(() => model(inputs), [inputs]);
  const ranked = useMemo(() => rank(m), [m]);
  const t3 = useMemo(() => top3(m), [m]);
  const top = t3[0];

  useEffect(() => {
    if (!id) return;
    adminApi.get(`/crm/plan-builder/scenarios/${id}`).then(({ data }) => {
      setInputs({ ...SAMPLE, ...data.inputs });
      setChecklist(data.checklist || {});
      setStoredVersion(data.model_version);
      setMeta({ id: data.id, name: data.name, client_company: data.client_company,
                advisor_notes: data.advisor_notes || "", status: data.status, lead_id: data.lead_id });
    }).catch((e) => toast.error(apiError(e)));
  }, [id]);

  useEffect(() => {
    if (!leadId) return;
    adminApi.get(`/crm/plan-builder/leads/${leadId}`).then(({ data }) => {
      setLead(data);
      setInputs({ ...SAMPLE, ...data.inputs });
      setStoredVersion(data.model_version);
      setMeta((s) => ({ ...s, client_company: data.company, lead_id: data.id,
                        name: `${data.company} – ${(data.inputs || {}).year || "2027"} options` }));
    }).catch((e) => toast.error(apiError(e)));
  }, [leadId]);

  const snapshot = useCallback(() => ({
    baseline_total: m.baseline.total, baseline_employer: m.baseline.employer,
    top3: t3.map((r) => ({ id: r.a.id, name: r.a.name, fit: Math.round(r.fit), elig: r.elig,
                           note: r.note, total: r.cost.total, employer: r.cost.employer,
                           employee: r.cost.employee, savTot: r.savTot })),
    computed_at: new Date().toISOString(),
  }), [m, t3]);

  const save = async (asNew) => {
    const company = meta.client_company.trim();
    if (!company) { toast.error("Client company is required."); return; }
    setSaving(true);
    const body = {
      name: meta.name.trim() || `${company} – ${inputs.year} options`,
      client_company: company, status: meta.status, inputs, checklist,
      advisor_notes: meta.advisor_notes, lead_id: meta.lead_id, model_version: MODEL_VERSION,
      result_snapshot: snapshot(),
    };
    try {
      if (meta.id && !asNew) {
        await adminApi.patch(`/crm/plan-builder/scenarios/${meta.id}`, body);
        toast.success("Scenario saved.");
      } else {
        const { data } = await adminApi.post("/crm/plan-builder/scenarios", body);
        setMeta((s) => ({ ...s, id: data.id }));
        toast.success("Scenario created.");
        navigate(`/admin/plan-builder/scenarios/${data.id}`);
      }
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  const isSample = JSON.stringify(inputs) === JSON.stringify(SAMPLE);
  const hsa = HSA_YEAR[inputs.year];
  const det = ranked.concat(m.results.filter((r) => r.elig === "Not eligible")).find((r) => r.a.id === detail)
    || m.results.find((r) => r.a.id === detail);
  const chartRows = m.results.filter((r) => r.elig !== "Not eligible");
  const chartMax = Math.max(m.baseline.total, ...chartRows.map((r) => r.cost.total));

  return (
    <div className="space-y-12" data-testid="plan-builder">
      <Tabs active="Builder" />
      <div className="sticky top-0 z-20 -mx-6 border-b border-cream/10 bg-[#0E1A13]/95 px-6 py-4 backdrop-blur">
        <div className="flex flex-wrap items-center gap-3">
          <h1 className="mr-auto font-serif text-2xl font-medium text-cream">Group Plan Builder</h1>
          {lead ? (
            <Link to={`/admin/plan-builder/leads/${lead.id}`} data-testid="pb-from-lead"
              className="border border-gold/40 px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.2em] text-gold">
              From Plan Check: {lead.company}
            </Link>
          ) : null}
          <button className={buttonClass} onClick={() => save(false)} disabled={saving} data-testid="pb-save">Save scenario</button>
          <button className={buttonClass} onClick={() => save(true)} disabled={saving} data-testid="pb-save-new">Save as new</button>
          {meta.id ? (
            <Link className={buttonClass} to={`/admin/plan-builder/scenarios/${meta.id}/summary`} data-testid="pb-summary">Client summary</Link>
          ) : null}
          <button className={buttonClass} onClick={() => setInputs(SAMPLE)} data-testid="pb-reset">Reset to sample</button>
        </div>
        <nav className="mt-3 flex flex-wrap gap-x-4 gap-y-1">
          {SECTIONS.map(([anchor, text]) => (
            <a key={anchor} href={`#${anchor}`} className="font-mono text-[10px] uppercase tracking-[0.15em] text-cream/35 hover:text-gold">
              {text}
            </a>
          ))}
        </nav>
      </div>

      {storedVersion && storedVersion !== MODEL_VERSION ? (
        <p className="border border-gold/40 bg-gold/10 p-3 text-sm text-gold" data-testid="pb-version-notice">
          Scored with model {storedVersion}; current model {MODEL_VERSION}
        </p>
      ) : null}
      {lead ? (
        <p className="border border-cream/15 bg-cream/5 p-4 text-sm leading-relaxed text-cream/65" data-testid="pb-prefill-banner">
          Pre-filled from the Plan Check. Defaults were used for: average age 41, census mix
          55/15/12/18, 80% / 50% contributions, PPO, plan year 2027, lowest wage $38,000, hybrid share
          40%. Replace them with census data.
        </p>
      ) : null}

      {/* 1 — EMPLOYER INPUTS */}
      <section className="space-y-6">
        <Head id="inputs" note={isSample
          ? "Sample employer loaded: 35 enrolled, 42 FTEs, Texas professional-services firm, average age 41, $650 PEPM, 9% renewal. Change any value to model a client."
          : "Custom employer. Reset to reload the sample employer."}>Employer inputs</Head>
        <Panel title="Group profile" testid="pb-profile">
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <Num name="enrolled" value={inputs.enrolled} onChange={(v) => set({ enrolled: v })}>Enrolled employees</Num>
            <Num name="fte" value={inputs.fte} onChange={(v) => set({ fte: v })}>Full-time equivalents (FTEs)</Num>
            <Pick name="state" value={inputs.state} onChange={(v) => set({ state: v })}
              options={[["TX", "Texas"], ["OTHER", "Other state (TX market figures as proxy)"]]}>State</Pick>
            <Pick name="industry" value={inputs.industry} onChange={(v) => set({ industry: v })}
              options={INDUSTRIES.map((i) => [i.value, i.label])}>Industry</Pick>
            <Num name="age" value={inputs.age} onChange={(v) => set({ age: v })}>Average age</Num>
            <Pick name="year" value={inputs.year} onChange={(v) => set({ year: v })}
              options={[["2026", "2026"], ["2027", "2027"]]}>Plan year</Pick>
            <Num name="wage" value={inputs.wage} onChange={(v) => set({ wage: v })}>Lowest full-time annual wage ($)</Num>
            <Num name="hybridShare" value={inputs.hybridShare} onChange={(v) => set({ hybridShare: v })}>Hybrid: % of enrollees in ICHRA class</Num>
          </div>
        </Panel>
        <Panel title="Census mix by tier (% of enrolled)" testid="pb-mix">
          <div className="grid gap-4 sm:grid-cols-4">
            <Num name="mixEE" value={inputs.mixEE} onChange={(v) => set({ mixEE: v })}>Employee only</Num>
            <Num name="mixES" value={inputs.mixES} onChange={(v) => set({ mixES: v })}>Employee + spouse</Num>
            <Num name="mixEC" value={inputs.mixEC} onChange={(v) => set({ mixEC: v })}>Employee + child(ren)</Num>
            <Num name="mixFam" value={inputs.mixFam} onChange={(v) => set({ mixFam: v })}>Family</Num>
          </div>
        </Panel>
        <Panel title="Current cost and contribution strategy" testid="pb-cost-inputs">
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <Num name="pepm" value={inputs.pepm} onChange={(v) => set({ pepm: v })}>Current total premium PEPM ($/enrolled/month)</Num>
            <Num name="renewal" value={inputs.renewal} onChange={(v) => set({ renewal: v })}>Renewal increase (%)</Num>
            <Pick name="mode" value={inputs.mode} onChange={(v) => set({ mode: v })}
              options={[["percent", "Percent of premium"], ["flat", "Flat defined contribution"]]}>Contribution strategy</Pick>
            <Pick name="design" value={inputs.design} onChange={(v) => set({ design: v })}
              options={[["ppo", "PPO (current baseline)"], ["hdhp", "HSA-qualified HDHP"], ["hmo", "HMO / EPO"]]}>Plan design</Pick>
            {inputs.mode === "percent" ? (
              <>
                <Num name="pctEE" value={inputs.pctEE} onChange={(v) => set({ pctEE: v })}>Employer % of employee-only cost</Num>
                <Num name="pctDep" value={inputs.pctDep} onChange={(v) => set({ pctDep: v })}>Employer % of dependent cost</Num>
              </>
            ) : (
              <Num name="flat" value={inputs.flat} onChange={(v) => set({ flat: v })}>Flat employer contribution ($/employee/month)</Num>
            )}
          </div>
        </Panel>
        <Panel title="Risk, health and priorities" testid="pb-risk">
          <div className="grid gap-4 sm:grid-cols-2">
            <Pick name="risk" value={inputs.risk} onChange={(v) => set({ risk: v })}
              options={[["low", "Low"], ["medium", "Medium"], ["high", "High"]]}>Risk tolerance</Pick>
            <Pick name="health" value={inputs.health} onChange={(v) => set({ health: v })}
              options={[["healthy", "Healthy"], ["average", "Average"], ["high", "High claims"]]}>Workforce health profile</Pick>
          </div>
          <div className="mt-5 flex flex-wrap gap-5">
            {[["pCost", "Priority: cost containment"], ["pRecruit", "Priority: recruiting and retention"],
              ["pSimple", "Priority: simplicity"], ["pChoice", "Priority: employee choice"]].map(([key, text]) => (
              <label key={key} className="flex items-center gap-2 text-sm text-cream/70">
                <input type="checkbox" checked={!!inputs[key]} data-testid={`pb-${key}`}
                  onChange={(e) => set({ [key]: e.target.checked })} className="h-4 w-4 accent-[#C6A15B]" />
                {text}
              </label>
            ))}
          </div>
        </Panel>
      </section>

      {/* 2 — RECOMMENDED */}
      <section className="space-y-5">
        <Head id="recommend" note={`For this employer, ${top.a.name} ranks first on fit; the three highest-value options are below.`}>
          Recommended options
        </Head>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {[[money(m.baseline.total), "Renewal at current funding", "Fully insured, annual, estimate"],
            [money(top.cost.total), `Top option: ${top.a.short}`, "Total annual cost, estimate"],
            [signed(top.savTot), "Total cost vs renewal", ""],
            [money(m.baseline.employer - top.cost.employer), "Employer change vs renewal", "Positive means lower employer cost"],
          ].map(([value, text, hint], i) => (
            <div key={text} className="border border-cream/10 bg-cream/5 p-5" data-testid={`pb-tile-${i}`}>
              <p className="font-serif text-2xl text-cream">{value}</p>
              <p className="mt-2 font-mono text-[10px] uppercase tracking-[0.18em] text-cream/45">{text}</p>
              {hint ? <p className="mt-1 text-xs text-cream/35">{hint}</p> : null}
            </div>
          ))}
        </div>
        <div className="divide-y divide-cream/10 border border-cream/10">
          {t3.map((r, i) => (
            <div key={r.a.id} className="p-5" data-testid={`pb-top-${r.a.id}`}>
              <div className="flex flex-wrap items-center gap-3">
                <span className="font-mono text-[10px] text-gold">{String(i + 1).padStart(2, "0")}</span>
                <h3 className="font-serif text-lg text-cream">{r.a.name}</h3>
                <Pill tone={r.elig === "Eligible" ? "good" : "warn"}>{r.elig}</Pill>
              </div>
              <p className="mt-2 text-sm leading-relaxed text-cream/65">{reason(r, inputs.risk, m.tol, inputs)}</p>
              <p className="mt-2 font-mono text-[10px] uppercase tracking-[0.18em] text-cream/35">
                Fit {Math.round(r.fit)} · {k(r.cost.total)} total
              </p>
            </div>
          ))}
        </div>
        <p className="border-l-2 border-gold bg-gold/5 p-4 text-sm leading-relaxed text-cream/60">
          Illustrative, not quotes. Every dollar figure here comes from relative cost factors applied to
          the employer's own renewal, using published industry ranges. Validate with census-based
          carrier, TPA and stop-loss quotes before presenting to a client.
        </p>
      </section>

      {/* 3 — COST */}
      <section className="space-y-5">
        <Head id="cost">Estimated annual cost by arrangement</Head>
        <div className="relative border border-cream/10 p-5">
          <div className="relative flex h-56 items-end gap-2" data-testid="pb-chart">
            <div className="absolute inset-x-0 border-t border-dashed border-gold"
              style={{ bottom: `${(m.baseline.total / chartMax) * 100}%` }}>
              <span className="absolute right-0 -top-5 font-mono text-[10px] text-gold">Renewal {k(m.baseline.total)}</span>
            </div>
            {chartRows.map((r) => (
              <div key={r.a.id} className="flex flex-1 flex-col justify-end" title={`${r.a.name}: ${money(r.cost.total)}`}>
                <div className="bg-[#C6A15B]" style={{ height: `${(r.cost.employer / chartMax) * 200}px` }} />
                <div className="bg-[#4F7A5B]" style={{ height: `${(r.cost.employee / chartMax) * 200}px` }} />
              </div>
            ))}
          </div>
          <div className="mt-2 flex gap-2">
            {chartRows.map((r) => (
              <p key={r.a.id} className="flex-1 text-center font-mono text-[9px] uppercase text-cream/40">
                {r.a.short}<br />{k(r.cost.total)}
              </p>
            ))}
          </div>
          <p className="mt-4 font-mono text-[10px] uppercase tracking-[0.18em] text-cream/35">
            <span className="mr-3"><span className="mr-1 inline-block h-2 w-2 bg-[#C6A15B]" />Employer</span>
            <span><span className="mr-1 inline-block h-2 w-2 bg-[#4F7A5B]" />Employee</span>
          </p>
        </div>
        <div className="overflow-x-auto border border-cream/10">
          <table className="w-full min-w-[760px]">
            <thead className="border-b border-cream/10"><tr>
              {["Arrangement", "Status", "Employer / yr", "Employee / yr", "Total / yr", "vs renewal", "Worst case / yr"].map((h) => <th key={h} className={th}>{h}</th>)}
            </tr></thead>
            <tbody className="divide-y divide-cream/5">
              {m.results.map((r) => (
                <tr key={r.a.id} data-testid={`pb-cost-${r.a.id}`}>
                  <td className={td}>{r.a.name}</td>
                  <td className={td}><Pill tone={r.elig === "Eligible" ? "good" : r.elig === "Stretch" ? "warn" : "neutral"}>{r.elig}</Pill></td>
                  <td className={td}>{money(r.cost.employer)}</td>
                  <td className={td}>{money(r.cost.employee)}</td>
                  <td className={td}>{money(r.cost.total)}</td>
                  <td className={td}>{signed(r.savTot)}</td>
                  <td className={td}>{money(r.cost.worst ?? r.cost.total)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="text-sm leading-relaxed text-cream/50">
          Worst case is the level-funded payment with no refund, or fixed costs plus claims at the 125%
          aggregate attachment for self-funded, RBP and captive. Employee cost covers contributions
          only, not deductibles or copays.
        </p>
      </section>

      {/* 4 — FIT */}
      <section className="space-y-4">
        <Head id="fit">Fit scores</Head>
        <div className="overflow-x-auto border border-cream/10">
          <table className="w-full min-w-[720px]">
            <thead className="border-b border-cream/10"><tr>
              {["Arrangement", "Fit", "Status", "Cost", "Risk fit", "Simplicity", "Recruiting", "Choice"].map((h) => <th key={h} className={th}>{h}</th>)}
            </tr></thead>
            <tbody className="divide-y divide-cream/5">
              {[...m.results].sort((a, b) => b.fit - a.fit).map((r) => (
                <tr key={r.a.id} data-testid={`pb-fit-${r.a.id}`}>
                  <td className={td}>{r.a.name}</td>
                  <td className={td}>{r.elig === "Not eligible" ? "–" : Math.round(r.fit)}</td>
                  <td className={td}>{r.elig}</td>
                  <td className={td}>{Math.round(r.sCost)}</td>
                  <td className={td}>{Math.round(r.sRisk)}</td>
                  <td className={td}>{Math.round(r.sAdmin)}</td>
                  <td className={td}>{Math.round(r.sRecruit)}</td>
                  <td className={td}>{Math.round(r.sChoice)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="text-sm leading-relaxed text-cream/50">
          0–100. Weighted blend of cost, risk fit, simplicity, recruiting and choice; each priority you
          switch on adds weight. Stretch options are scored at 85%.
        </p>
      </section>

      {/* 5 — DETAIL */}
      <section className="space-y-4">
        <Head id="detail">Arrangement detail</Head>
        <select value={detail} onChange={(e) => setDetail(e.target.value)} className={inputClass} data-testid="pb-detail-select">
          {ARRS.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
        </select>
        <Panel title={det.a.name} testid="pb-detail">
          <dl className="grid gap-4 sm:grid-cols-2">
            {[["Type", det.a.kind], ["Eligibility rule", det.a.rule], ["This employer", `${det.elig}: ${det.note}`],
              ["Risk level", det.a.riskLabel], ["Admin burden", det.a.adminLabel], ["Cost behavior", det.a.behavior],
              ["Modeled cost factor", `${det.factor.toFixed(2)} × fully insured renewal, estimate`],
              ["Employer / employee / total", `${money(det.cost.employer)} / ${money(det.cost.employee)} / ${money(det.cost.total)}`],
              ["Worst case", money(det.cost.worst ?? det.cost.total)],
              ["Fit score", det.elig === "Not eligible" ? "–" : Math.round(det.fit)]].map(([t, v]) => (
              <div key={t}>
                <dt className={label}>{t}</dt>
                <dd className="mt-1 text-sm leading-relaxed text-cream/75">{v}</dd>
              </div>
            ))}
          </dl>
          <div className="mt-6 grid gap-6 sm:grid-cols-2">
            {[["Pros", det.a.pros], ["Cons", det.a.cons]].map(([t, list]) => (
              <div key={t}>
                <p className={label}>{t}</p>
                <ul className="mt-2 space-y-2">
                  {list.map((x) => <li key={x} className="text-sm leading-relaxed text-cream/65">{x}</li>)}
                </ul>
              </div>
            ))}
          </div>
        </Panel>
      </section>

      {/* 6 — COMPLIANCE */}
      <section className="space-y-4">
        <Head id="compliance">Compliance checks for this employer</Head>
        <div className="space-y-3">
          {[
            ["Market segment", m.small
              ? (inputs.state === "TX"
                ? "Small group: Texas defines small employers as 2–50 employees (Tex. Ins. Code §1501.002)."
                : "Small group: 50 or fewer employees in most states.")
              : "Large group: 51+ employees, experience-rated.", null],
            ["ALE", m.ale
              ? `${Math.round(m.fte)} FTEs: employer mandate applies. No-offer exposure under 4980H(a) is about ${money(m.penaltyA)} a year at the 2026 rate of $3,340 per FT employee beyond 30.`
              : `${Math.round(m.fte)} FTEs: under 50, so no employer mandate penalty, and QSEHRA is available if no group plan is offered.`, null],
            ["Group plan affordability",
              `Employee contribution about ${money(m.coreEE)}/mo vs a ${(m.affPct * 100).toFixed(2)}% limit of ${money(m.affLimit)}/mo on a ${money(Number(inputs.wage))} wage (rate-of-pay safe harbor). ${m.ale ? "Required for ALEs." : "Informational under 50 FTEs."}`,
              m.coreEE <= m.affLimit ? "Affordable" : "Not affordable"],
            ["ICHRA affordability proxy",
              `Age-${Math.round(Number(inputs.age))} benchmark silver about ${money(m.indivEE)}/mo minus a ${money(m.allowEE)}/mo allowance leaves ${money(Math.max(0, m.ichraEE))}/mo. The real test uses each employee's lowest-cost silver plan and age.`, null],
            ["PCORI",
              `About ${Math.round(m.lives)} covered lives × $3.84 = ${money(m.pcori)}, filed by the employer on Form 720 by July 31. $3.84 covers plan years ending Oct 2025–Sep 2026; the amount for later plan years was not yet published as of Oct 30, 2026. Insured plans: the carrier pays.`, null],
            ["HSA-qualified HDHP test",
              `${inputs.year}: minimum deductible ${hsa.ded}; out-of-pocket max ${hsa.oop}; HSA limit ${hsa.hsa} (self-only / family). Family HDHPs also need an embedded individual OOP max no higher than the ACA limit.`, null],
          ].map(([title, text, chip]) => (
            <div key={title} className="border border-cream/10 p-4" data-testid={`pb-compliance-${title.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
              <div className="flex flex-wrap items-center gap-3">
                <p className={label}>{title}</p>
                {chip ? <Pill tone={chip === "Affordable" ? "good" : "warn"}>{chip}</Pill> : null}
              </div>
              <p className="mt-2 text-sm leading-relaxed text-cream/70">{text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* 7 — DESIGN LAYER */}
      <section className="space-y-4">
        <Head id="design">Plan design layer</Head>
        <div className="overflow-x-auto border border-cream/10">
          <table className="w-full min-w-[520px]">
            <thead className="border-b border-cream/10"><tr>
              {["Design", "Factor", `Rules for ${inputs.year}`].map((h) => <th key={h} className={th}>{h}</th>)}
            </tr></thead>
            <tbody className="divide-y divide-cream/5">
              {DESIGN_LAYER.map(([name, factor, rule]) => (
                <tr key={name}>
                  <td className={td}>{name}</td>
                  <td className={td}>{factor} {DESIGN_F[inputs.design] === Number(factor) ? <Pill tone="good">Selected</Pill> : null}</td>
                  <td className={td}>{rule === "Baseline design" ? rule : `${rule} — deductible ${hsa.ded}, OOP ${hsa.oop}`}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="text-sm leading-relaxed text-cream/50">
          The selected design multiplies every group-based arrangement; ICHRA, QSEHRA and MEC/MVP are
          unaffected, and DPC always uses the HDHP. HDHP factor is KFF 2025 HDHP/SO vs PPO single
          premium ($8,620 vs $9,818); HMO/EPO is an OHC planning assumption.
        </p>
      </section>

      {/* 8 — REFERENCE */}
      <section className="space-y-4">
        <Head id="reference">2026 and 2027 reference figures</Head>
        <div className="overflow-x-auto border border-cream/10">
          <table className="w-full min-w-[720px]">
            <thead className="border-b border-cream/10"><tr>
              {["Item", "2026", "2027", "Source"].map((h) => <th key={h} className={th}>{h}</th>)}
            </tr></thead>
            <tbody className="divide-y divide-cream/5">
              {REF_FIGURES.map((row) => (
                <tr key={row[0]}>{row.map((cell, i) => <td key={i} className={td}>{cell}</td>)}</tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* 9 — ASSUMPTIONS */}
      <section className="space-y-4">
        <Head id="assumptions">Modeling assumptions</Head>
        <ul className="space-y-2">
          {FORMULAS.map((f) => <li key={f} className="font-mono text-xs text-cream/60">{f}</li>)}
        </ul>
        <div className="overflow-x-auto border border-cream/10">
          <table className="w-full min-w-[720px]">
            <thead className="border-b border-cream/10"><tr>
              {["Assumption", "Value used", "Basis"].map((h) => <th key={h} className={th}>{h}</th>)}
            </tr></thead>
            <tbody className="divide-y divide-cream/5">
              {ASSUMPTIONS.map(([a, value, basis]) => (
                <tr key={a}>
                  <td className={td}>{a}</td>
                  <td className={td}>{value === "__HYBRID__"
                    ? `${Math.round(m.s * 100)}% of enrollees in the ICHRA class, rest on the core group plan (live from input)`
                    : value}</td>
                  <td className={td}>
                    {basis}
                    {basis.includes("OHC planning assumption")
                      ? <span className="ml-2 border border-gold/40 px-1.5 py-0.5 font-mono text-[9px] uppercase tracking-[0.15em] text-gold">OHC assumption</span>
                      : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* 10 — CHECKLIST */}
      <section className="space-y-4">
        <Head id="checklist">Data to gather for a real proposal</Head>
        <div className="grid gap-5 lg:grid-cols-2">
          {CHECKLIST.map(([group, items]) => (
            <Panel key={group} title={group} testid={`pb-checklist-${group.slice(0, 8).toLowerCase()}`}>
              <ul className="space-y-3">
                {items.map((item) => {
                  const key = `${group}|${item}`;
                  return (
                    <li key={item}>
                      <label className="flex gap-3 text-sm leading-relaxed text-cream/70">
                        <input type="checkbox" checked={!!checklist[key]} className="mt-1 h-4 w-4 shrink-0 accent-[#C6A15B]"
                          onChange={(e) => setChecklist((c) => ({ ...c, [key]: e.target.checked }))} />
                        {item}
                      </label>
                    </li>
                  );
                })}
              </ul>
            </Panel>
          ))}
        </div>
      </section>

      {/* 11 — FACTORS */}
      <section className="space-y-4">
        <Head id="factors">Decision factors</Head>
        <div className="overflow-x-auto border border-cream/10">
          <table className="w-full min-w-[720px]">
            <thead className="border-b border-cream/10"><tr>
              {["Factor", "Question to answer", "Points toward"].map((h) => <th key={h} className={th}>{h}</th>)}
            </tr></thead>
            <tbody className="divide-y divide-cream/5">
              {DECISION_FACTORS.map((row) => (
                <tr key={row[0]}>{row.map((cell, i) => <td key={i} className={td}>{cell}</td>)}</tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* 12 — SOURCES */}
      <section className="space-y-4">
        <Head id="sources">Sources</Head>
        <ul className="space-y-2">
          {SOURCES.map(([title, site, url]) => (
            <li key={url} className="text-sm text-cream/70">
              <a href={url} target="_blank" rel="noreferrer" className="hover:text-gold">{title}</a>
              <span className="ml-2 font-mono text-[10px] uppercase tracking-[0.15em] text-cream/35">{site}</span>
            </li>
          ))}
        </ul>
      </section>

      <Panel title="Scenario details" testid="pb-scenario-meta">
        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <label className={label} htmlFor="pb-name">Scenario name</label>
            <input id="pb-name" value={meta.name} data-testid="pb-scenario-name" className={`${inputClass} mt-1.5`}
              placeholder={`${meta.client_company || "Client"} – ${inputs.year} options`}
              onChange={(e) => setMeta({ ...meta, name: e.target.value })} />
          </div>
          <div>
            <label className={label} htmlFor="pb-company">Client company</label>
            <input id="pb-company" value={meta.client_company} data-testid="pb-client-company" className={`${inputClass} mt-1.5`}
              onChange={(e) => setMeta({ ...meta, client_company: e.target.value })} />
          </div>
          <div className="sm:col-span-2">
            <label className={label} htmlFor="pb-notes">Advisor notes (shown on the client summary)</label>
            <textarea id="pb-notes" rows={3} value={meta.advisor_notes} data-testid="pb-advisor-notes"
              className={`${inputClass} mt-1.5`} onChange={(e) => setMeta({ ...meta, advisor_notes: e.target.value })} />
          </div>
          <Pick name="status" value={meta.status} onChange={(v) => setMeta({ ...meta, status: v })}
            options={[["draft", "Draft"], ["presented", "Presented"]]}>Status</Pick>
        </div>
      </Panel>
    </div>
  );
}
